import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logLoginEvent } from "../misc/firebase";
import { isErrorWithMessage } from "../misc/isErrorWithMessage";
import { useQuery } from "../misc/useQuery";
import { auth } from "./auth";
import { useAuthContext } from "./AuthContext";
import { getSignInErrorDetails } from "./signInErrorDetails";

export type LoginMode = "EMAIL_PASSOWORD" | "EMAIL_LINK" | "EMAIL_LINK_CONFIRM" | undefined;

export interface UseLoginFlowResult {
  readonly mode: LoginMode;
  readonly setMode: (mode: LoginMode) => void;
  readonly isSigningIn: boolean;
  readonly enqueueLoginError: (reason: string, message?: string) => void;
  readonly handleGoogleLogin: () => Promise<void>;
  readonly handleTravel360Login: () => Promise<void>;
  readonly handleEmailLogin: (email: string) => Promise<void>;
}

export function useLoginFlow(initialMode: LoginMode): UseLoginFlowResult {
  const navigate = useNavigate();
  const searchQuery = useQuery();
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState<LoginMode>(initialMode);
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    if (user) {
      const navigateTo = searchQuery.get("redirectTo") ?? "/";
      navigate(navigateTo);
    } else if (auth.isSignInWithEmailLink(window.location.href)) {
      setMode("EMAIL_LINK_CONFIRM");
    }
  }, [user, searchQuery, navigate]);

  useEffect(() => {
    auth
      .getRedirectResult()
      .then((result) => {
        if (result) {
          const providerId = result.providerId ?? "";
          logLoginEvent(providerId.includes("google") ? "google" : "travel360");
        }
      })
      .catch((error) => {
        enqueueSnackbar({
          variant: "error",
          message: `Logowanie nie powiodło się. Powód: ${getSignInErrorDetails(error)}`,
          autoHideDuration: 15000,
        });
      });
  }, [enqueueSnackbar]);

  const enqueueLoginError = (reason: string, message = "Logowanie nie powiodło się") =>
    enqueueSnackbar({
      variant: "error",
      message: `${message}. Powód: ${reason}`,
      autoHideDuration: 15000,
    });

  const handleGoogleLogin = async () => {
    if (isSigningIn) {
      return;
    }
    setIsSigningIn(true);
    try {
      await auth.signInWithGoogle();
      logLoginEvent("google");
    } catch (error) {
      enqueueLoginError(isErrorWithMessage(error) ? error.message : JSON.stringify(error));
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleTravel360Login = async () => {
    if (isSigningIn) {
      return;
    }
    setIsSigningIn(true);
    try {
      await auth.signInWithTravel360();
      logLoginEvent("travel360");
    } catch (error) {
      enqueueLoginError(isErrorWithMessage(error) ? error.message : JSON.stringify(error));
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailLogin = async (email: string) => {
    try {
      await auth.signInWithEmail(email);
    } catch (error) {
      enqueueLoginError(
        isErrorWithMessage(error) ? error.message : JSON.stringify(error),
        "Wysyłanie linku do logowania nie powiodło się",
      );
    }
  };

  return { mode, setMode, isSigningIn, enqueueLoginError, handleGoogleLogin, handleTravel360Login, handleEmailLogin };
}
